import React, { useState, useEffect, useCallback } from 'react';
import { useFlightCalculationsContext } from '../contexts/FlightCalculationsContext';
import styles from '../styles/calculator.module.css';

const FlightParameterControls: React.FC = () => {
  const { parameters, unitSystem, setParameters } = useFlightCalculationsContext();
  
  // Initialize parameters with default values if not already set
  useEffect(() => {
    const hasInitialized = sessionStorage.getItem('parametersInitialized');
    
    if (!hasInitialized) {
      setParameters({
        ...parameters, // Keep any other parameters
        altitude: 121.92, // Default altitude 121.92m (400ft)
        radius: 30, // Default radius 30m
        velocity: 5 // Default velocity 5m/s
      });
      sessionStorage.setItem('parametersInitialized', 'true');
    }
  }, [setParameters, parameters]);
  
  // Area constraints state
  const [areaConstraintMode, setAreaConstraintMode] = useState<'none' | 'limitAltitude' | 'limitSize'>('none');
  const [maxSecuredArea, setMaxSecuredArea] = useState<number>(75); // Default value in meters/feet
  
  // Calculated constraints
  const [maxCalculatedAltitude, setMaxCalculatedAltitude] = useState<number | null>(null);
  const [maxCalculatedRadius, setMaxCalculatedRadius] = useState<number | null>(null);

  const handleSliderChange = (paramName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    
    if (paramName === 'radius') {
      // Use the custom radius handler for constraint handling
      handleRadiusChange(value);
    } else if (paramName === 'altitude') {
      // Use the custom altitude handler for constraint handling
      handleAltitudeChange(value);
    } else {
      // For other parameters, use the standard handler
      setParameters({ [paramName]: value });
    }
  };

  const handleInputChange = (paramName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow empty values during typing for better editing experience
    if (e.target.value === '') {
      return; // Don't update state for empty values during editing
    }
    
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      if (paramName === 'radius') {
        // Use the custom radius handler for constraint handling
        handleRadiusChange(value);
      } else if (paramName === 'altitude') {
        // Use the custom altitude handler for constraint handling
        handleAltitudeChange(value);
      } else {
        // For other parameters, use the standard handler
        setParameters({ [paramName]: value });
      }
    }
  };
  
  // Prevent spinner behavior on number inputs
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent arrow keys from changing the value (spinner behavior)
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  };
  
  const handleInputBlur = (paramName: keyof typeof parameters, min: number, max: number) => (e: React.FocusEvent<HTMLInputElement>) => {
    let value = parseFloat(e.target.value);
    if (isNaN(value)) {
      // Safely get the parameter value ensuring it's a number
      const currentValue = typeof parameters[paramName] === 'number' ? parameters[paramName] as number : min;
      value = currentValue;
    }
    // Constrain to min/max values
    value = Math.max(min, Math.min(max, value));
    setParameters({ [paramName]: value });
  };

  const getUnitLabel = (paramName?: string) => unitSystem === 'imperial' ? 'ft' : 'm';
  const getVelocityUnit = () => 'm/s';

  // Function to calculate max altitude based on radius
  const calculateMaxAltitude = useCallback((radius: number): number => {
    // Formula: altitude = (restrictedWidth × 100) / 33
    // Where restrictedWidth = maxRestrictedArea - flightRadius - softBuffer - hardOffset
    
    const hardGeofenceOffset = unitSystem === 'imperial' ? 3.28084 : 1; // 1 meter in ft or m
    const maxRestrictedArea = maxSecuredArea;
    const flightRadius = radius;
    const faaLimit = unitSystem === 'imperial' ? 400 : 122; // FAA altitude limit
    
    // We need to test each altitude range because the soft buffer changes in steps
    let maxAltitude = 0;
    
    // For 0-100ft range (or equivalent in meters)
    const softBuffer1 = unitSystem === 'imperial' ? 5 : 1.524; // 5ft or 1.524m
    const restrictedWidth1 = maxRestrictedArea - flightRadius - softBuffer1 - hardGeofenceOffset;
    if (restrictedWidth1 > 0) {
      const testAlt1 = (restrictedWidth1 * 100) / 33;
      // Check if it's within the range limit
      if (testAlt1 <= (unitSystem === 'imperial' ? 100 : 30.48)) {
        maxAltitude = testAlt1;
      } else {
        maxAltitude = unitSystem === 'imperial' ? 100 : 30.48; // Cap at range limit
      }
    }
    
    // For 101-200ft range (or equivalent in meters)
    const softBuffer2 = unitSystem === 'imperial' ? 10 : 3.048; // 10ft or 3.048m
    const restrictedWidth2 = maxRestrictedArea - flightRadius - softBuffer2 - hardGeofenceOffset;
    if (restrictedWidth2 > 0) {
      const testAlt2 = (restrictedWidth2 * 100) / 33;
      const minAlt2 = unitSystem === 'imperial' ? 101 : 30.79; // Range start
      const maxAlt2 = unitSystem === 'imperial' ? 200 : 60.96; // Range limit
      
      // Check if it's above the minimum for this range and greater than previous calculations
      if (testAlt2 >= minAlt2 && testAlt2 > maxAltitude) {
        maxAltitude = Math.min(testAlt2, maxAlt2); // Cap at range limit
      }
    }
    
    // For 201-300ft range (or equivalent in meters)
    const softBuffer3 = unitSystem === 'imperial' ? 15 : 4.572; // 15ft or 4.572m
    const restrictedWidth3 = maxRestrictedArea - flightRadius - softBuffer3 - hardGeofenceOffset;
    if (restrictedWidth3 > 0) {
      const testAlt3 = (restrictedWidth3 * 100) / 33;
      const minAlt3 = unitSystem === 'imperial' ? 201 : 61.27; // Range start
      const maxAlt3 = unitSystem === 'imperial' ? 300 : 91.44; // Range limit
      
      // Check if it's above the minimum for this range and greater than previous calculations
      if (testAlt3 >= minAlt3 && testAlt3 > maxAltitude) {
        maxAltitude = Math.min(testAlt3, maxAlt3); // Cap at range limit
      }
    }
    
    // For 301-400ft range (or equivalent in meters)
    const softBuffer4 = unitSystem === 'imperial' ? 20 : 6.096; // 20ft or 6.096m
    const restrictedWidth4 = maxRestrictedArea - flightRadius - softBuffer4 - hardGeofenceOffset;
    if (restrictedWidth4 > 0) {
      const testAlt4 = (restrictedWidth4 * 100) / 33;
      const minAlt4 = unitSystem === 'imperial' ? 301 : 91.75; // Range start
      const maxAlt4 = unitSystem === 'imperial' ? 400 : 121.92; // Range limit
      
      // Check if it's above the minimum for this range and greater than previous calculations
      if (testAlt4 >= minAlt4 && testAlt4 > maxAltitude) {
        maxAltitude = Math.min(testAlt4, maxAlt4); // Cap at range limit
      }
    }
    
    // Apply FAA limit
    maxAltitude = Math.min(maxAltitude, faaLimit);
    
    return maxAltitude > 0 ? Math.round(maxAltitude) : 0;
  }, [maxSecuredArea, unitSystem]);
  
  // Function to calculate max radius based on altitude
  const calculateMaxRadius = useCallback((altitude: number): number => {
    // Formula: flightRadius = maxRestrictedArea - softBuffer - hardOffset - restrictedWidth
    // Where restrictedWidth = (altitude/100) × 33
    
    const hardGeofenceOffset = unitSystem === 'imperial' ? 3.28084 : 1; // 1 meter in ft or m
    const maxRestrictedArea = maxSecuredArea;
    
    // Determine the soft buffer based on altitude range
    let softBuffer;
    if (altitude <= (unitSystem === 'imperial' ? 100 : 30.48)) {
      softBuffer = unitSystem === 'imperial' ? 5 : 1.524; // 5ft or 1.524m
    } else if (altitude <= (unitSystem === 'imperial' ? 200 : 60.96)) {
      softBuffer = unitSystem === 'imperial' ? 10 : 3.048; // 10ft or 3.048m
    } else if (altitude <= (unitSystem === 'imperial' ? 300 : 91.44)) {
      softBuffer = unitSystem === 'imperial' ? 15 : 4.572; // 15ft or 4.572m
    } else {
      softBuffer = unitSystem === 'imperial' ? 20 : 6.096; // 20ft or 6.096m
    }
    
    // Calculate restricted area width based on altitude
    const restrictedAreaWidth = (altitude / 100) * 33;
    
    // Calculate maximum flight radius
    const maxRadius = Math.max(0, maxRestrictedArea - softBuffer - hardGeofenceOffset - restrictedAreaWidth);
    
    return Math.round(maxRadius);
  }, [maxSecuredArea, unitSystem]);

  // Flag to prevent recursive updates
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Custom handler for radius changes with constraint handling
  const handleRadiusChange = useCallback((newRadius: number) => {
    // Prevent recursive updates
    if (isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      // Handle radius change based on constraint mode
      if (areaConstraintMode === 'limitAltitude') {
        // When radius changes, recalculate altitude based on constraints
        const newMaxAltitude = calculateMaxAltitude(newRadius);
        
        if (newMaxAltitude > 0) {
          // Update both radius and altitude
          setParameters({
            ...parameters,
            radius: newRadius,
            altitude: newMaxAltitude
          });
          
          // Update the displayed max calculated altitude
          setMaxCalculatedAltitude(newMaxAltitude);
        } else {
          // If the calculation returns an invalid value, just update the radius
          setParameters({
            ...parameters,
            radius: newRadius
          });
        }
      } else {
        // Normal radius update
        setParameters({
          ...parameters,
          radius: newRadius
        });
      }
      
      // If we're not in a constraint mode but we have calculated values from before,
      // we should recalculate them so they're up-to-date if the user re-enables constraints
      if (areaConstraintMode === 'none') {
        if (maxCalculatedRadius !== null) {
          setMaxCalculatedRadius(calculateMaxRadius(parameters.altitude));
        }
        if (maxCalculatedAltitude !== null) {
          setMaxCalculatedAltitude(calculateMaxAltitude(newRadius));
        }
      }
    } catch (error) {
      console.error('Error in handleRadiusChange:', error);
      // Fallback to just updating radius
      setParameters({
        ...parameters,
        radius: newRadius
      });
    }
    
    // Allow updates again after a short delay
    setTimeout(() => {
      setIsUpdating(false);
    }, 10);
    
  }, [areaConstraintMode, parameters, setParameters, calculateMaxAltitude, 
      maxCalculatedRadius, maxCalculatedAltitude, calculateMaxRadius, isUpdating]);

  // Custom handler for altitude changes with constraint handling
  const handleAltitudeChange = useCallback((newAltitude: number) => {
    // Prevent recursive updates
    if (isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      // Handle altitude change based on constraint mode
      if (areaConstraintMode === 'limitSize') {
        // When altitude changes, recalculate radius based on constraints
        const newMaxRadius = calculateMaxRadius(newAltitude);
        
        if (newMaxRadius > 0) {
          // Update both altitude and radius
          setParameters({
            ...parameters,
            altitude: newAltitude,
            radius: newMaxRadius
          });
          
          // Update the displayed max calculated radius
          setMaxCalculatedRadius(newMaxRadius);
        } else {
          // If the calculation returns an invalid value, just update the altitude
          setParameters({
            ...parameters,
            altitude: newAltitude
          });
        }
      } else {
        // Normal altitude update
        setParameters({
          ...parameters,
          altitude: newAltitude
        });
      }
      
      // If we're not in a constraint mode but we have calculated values from before,
      // we should recalculate them so they're up-to-date if the user re-enables constraints
      if (areaConstraintMode === 'none') {
        if (maxCalculatedRadius !== null) {
          setMaxCalculatedRadius(calculateMaxRadius(newAltitude));
        }
        if (maxCalculatedAltitude !== null) {
          setMaxCalculatedAltitude(calculateMaxAltitude(parameters.radius));
        }
      }
    } catch (error) {
      console.error('Error in handleAltitudeChange:', error);
      // Fallback to just updating altitude
      setParameters({
        ...parameters,
        altitude: newAltitude
      });
    }
    
    // Allow updates again after a short delay
    setTimeout(() => {
      setIsUpdating(false);
    }, 10);
    
  }, [areaConstraintMode, parameters, setParameters, calculateMaxRadius, 
      maxCalculatedRadius, maxCalculatedAltitude, calculateMaxAltitude, isUpdating]);

  // Calculate constraints based on area and mode when modes change
  useEffect(() => {
    if (areaConstraintMode === 'none' || !maxSecuredArea) {
      setMaxCalculatedAltitude(null);
      setMaxCalculatedRadius(null);
      return;
    }
    
    if (areaConstraintMode === 'limitAltitude') {
      const maxAltitude = calculateMaxAltitude(parameters.radius);
      setMaxCalculatedAltitude(maxAltitude);
      setMaxCalculatedRadius(null);
    } 
    else if (areaConstraintMode === 'limitSize') {
      const maxRadius = calculateMaxRadius(parameters.altitude);
      setMaxCalculatedRadius(maxRadius);
      setMaxCalculatedAltitude(null);
    }
  }, [areaConstraintMode, maxSecuredArea, calculateMaxAltitude, calculateMaxRadius, parameters.radius, parameters.altitude]);
  
  // Enforce constraints on parameters
  useEffect(() => {
    // Only enforce if we have constraints and they're active
    if (areaConstraintMode === 'limitAltitude' && maxCalculatedAltitude !== null) {
      // If current altitude exceeds calculated max, adjust it
      if (parameters.altitude > maxCalculatedAltitude) {
        setParameters({ altitude: maxCalculatedAltitude });
      }
    }
    
    if (areaConstraintMode === 'limitSize' && maxCalculatedRadius !== null) {
      // If current radius exceeds calculated max, adjust it
      if (parameters.radius > maxCalculatedRadius) {
        setParameters({ radius: maxCalculatedRadius });
      }
    }
  }, [areaConstraintMode, maxCalculatedAltitude, maxCalculatedRadius, parameters.altitude, parameters.radius, setParameters]);

  return (
    <div className={styles.inputGroupsContainer}>
        <div className={styles.parameterControl}>
          <div className={styles.labelContainer}>
            <label>
              Flight Radius
              <span className={styles.helpIcon}>?</span>
            </label>
          </div>
          <div className={styles.parameterDescription}>
            Horizontal distance from takeoff point
          </div>
          <div className={styles.sliderContainer}>
            <input
              type="range"
              min={unitSystem === 'imperial' ? 10 : 3}
              max={unitSystem === 'imperial' ? 500 : 152}
              value={parameters.radius}
              onChange={handleSliderChange('radius')}
              className={styles.slider}
            />
            <div className={styles.inputWithUnit}>
              <input
                type="number"
                value={Math.round(parameters.radius).toString()}
                onChange={handleInputChange('radius')}
                onBlur={handleInputBlur('radius', unitSystem === 'imperial' ? 10 : 3, unitSystem === 'imperial' ? 500 : 152)}
                onKeyDown={handleKeyDown}
                min={unitSystem === 'imperial' ? 10 : 3}
                max={unitSystem === 'imperial' ? 500 : 152}
                step="1"
                className={styles.numberInput}
              />
              <span className={styles.unitLabel}>{getUnitLabel()}</span>
            </div>
          </div>
        </div>
        
        <div className={styles.parameterControl}>
          <div className={styles.labelContainer}>
            <label>
              Flight Altitude
              <span className={styles.helpIcon}>?</span>
            </label>
          </div>
          <div className={styles.parameterDescription}>
            Height above ground level - Max: {unitSystem === 'imperial' ? '400 ft' : '121.92 meters'}
          </div>
          <div className={styles.sliderContainer}>
            <input
              type="range"
              min={0}
              max={unitSystem === 'imperial' ? 400 : 122}
              value={parameters.altitude}
              onChange={handleSliderChange('altitude')}
              className={styles.slider}
            />
            <div className={styles.inputWithUnit}>
              <input
                type="number"
                value={Math.round(parameters.altitude).toString()}
                onChange={handleInputChange('altitude')}
                onBlur={handleInputBlur('altitude', 0, unitSystem === 'imperial' ? 400 : 122)}
                onKeyDown={handleKeyDown}
                min={0}
                max={unitSystem === 'imperial' ? 400 : 122}
                step="1"
                className={styles.numberInput}
              />
              <span className={styles.unitLabel}>{getUnitLabel()}</span>
            </div>
          </div>
        </div>
        
        <div className={styles.parameterControl}>
          <div className={styles.labelContainer}>
            <label>
              Maximum Velocity
              <span className={styles.helpIcon}>?</span>
            </label>
          </div>
          <div className={styles.parameterDescription}>
            The maximum speed your drone will reach during flight
          </div>
          <div className={styles.sliderContainer}>
            <input
              type="range"
              min={1}
              max={8}
              step={0.1}
              value={parameters.velocity}
              onChange={handleSliderChange('velocity')}
              className={styles.slider}
            />
            <div className={styles.inputWithUnit}>
              <input
                type="number"
                value={parameters.velocity.toFixed(1)}
                onChange={handleInputChange('velocity')}
                onBlur={handleInputBlur('velocity', 1, 8)}
                min={1}
                max={8}
                step={0.1}
                className={styles.numberInput}
              />
              <span className={styles.unitLabel}>{getVelocityUnit()}</span>
            </div>
          </div>
        </div>
        
        <div className={`${styles.parameterControl} ${styles.areaConstraintControl}`}>
          <div className={styles.labelContainer}>
            <label>
              Area Constraints
              <span className={styles.helpIcon}>?</span>
            </label>
          </div>
          <div className={styles.parameterDescription}>
            Define constraints based on your maximum available secured area
          </div>
          
          <div className={styles.sliderContainer}>
            <input
              type="range"
              min={unitSystem === 'imperial' ? 16.4 : 5}
              max={unitSystem === 'imperial' ? 820.21 : 250}
              value={maxSecuredArea}
              onChange={(e) => setMaxSecuredArea(parseInt(e.target.value))}
              className={styles.slider}
            />
            <div className={styles.inputWithUnit}>
              <input
                type="number"
                value={Math.round(maxSecuredArea).toString()}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value)) {
                    setMaxSecuredArea(value);
                  }
                }}
                onBlur={(e) => {
                  let value = parseInt(e.target.value);
                  if (isNaN(value)) value = 75;
                  value = Math.max(
                    unitSystem === 'imperial' ? 16.4 : 5,
                    Math.min(unitSystem === 'imperial' ? 820.21 : 250, value)
                  );
                  setMaxSecuredArea(value);
                }}
                onKeyDown={handleKeyDown}
                min={unitSystem === 'imperial' ? 16.4 : 5}
                max={unitSystem === 'imperial' ? 820.21 : 250}
                step="1"
                className={styles.numberInput}
              />
              <span className={styles.unitLabel}>{getUnitLabel()}</span>
            </div>
          </div>
          
          <div className={styles.constraintModeSelector}>
            <label>Constraint Mode:</label>
            <div className={styles.constraintOptions}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="constraintMode"
                  value="none"
                  checked={areaConstraintMode === 'none'}
                  onChange={() => setAreaConstraintMode('none')}
                  className={styles.radioInput}
                />
                No constraints (normal operation)
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="constraintMode"
                  value="limitAltitude"
                  checked={areaConstraintMode === 'limitAltitude'}
                  onChange={() => setAreaConstraintMode('limitAltitude')}
                  className={styles.radioInput}
                />
                Limit altitude, optimize size
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="constraintMode"
                  value="limitSize"
                  checked={areaConstraintMode === 'limitSize'}
                  onChange={() => setAreaConstraintMode('limitSize')}
                  className={styles.radioInput}
                />
                Limit size, optimize altitude
              </label>
            </div>
          </div>
          
          {areaConstraintMode !== 'none' && (
            <div className={styles.constraintResultWrapper}>
              {areaConstraintMode === 'limitAltitude' && maxCalculatedAltitude !== null && (
                <div className={styles.constraintResult}>
                  <span className={styles.resultLabel}>Maximum Altitude:</span>
                  <span className={styles.resultValue}>
                    {maxCalculatedAltitude} {getUnitLabel()}
                  </span>
                  <div className={styles.constraintInfo}>
                      Keeping your flight radius at {parameters.radius} {getUnitLabel()}
                  </div>
                </div>
              )}
              
              {areaConstraintMode === 'limitSize' && maxCalculatedRadius !== null && (
                <div className={styles.constraintResult}>
                  <span className={styles.resultLabel}>Maximum Flight Radius:</span>
                  <span className={styles.resultValue}>
                    {maxCalculatedRadius} {getUnitLabel()}
                  </span>
                  <div className={styles.constraintInfo}>
                      Keeping your altitude at {parameters.altitude} {getUnitLabel()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
    </div>
  );
};

export default FlightParameterControls;
